import { clowncar } from 'types';
import { consumer } from '@tock/coretypes';
import ClowncarAppConstants from 'constants/ClowncarAppConstants';
import protos from 'utils/protos';

export type ConfigurationFetchMode = 'ACTIVE' | 'ALL' | 'ARCHIVED';

const { ConsumerPageConfig, NamedConfiguration, CollectionDataConfiguration } = protos;

export function fetchAllDraftConfigs(): ApiAction {
  return {
    TockApi: {
      getListProto: '/internal/consumer/page/draft',
      type: ClowncarAppConstants.CONSUMER_PAGE_CONFIG_DRAFT_GET_ALL,
    },
  };
}

export function fetchDraftConfig(id: string): ApiAction {
  return {
    TockApi: {
      getProto: `/internal/consumer/page/draft/${id}`,
      type: ClowncarAppConstants.CONSUMER_PAGE_CONFIG_DRAFT_GET,
    },
  };
}

export function fetchConfig(id: string): ApiAction {
  return {
    TockApi: {
      getProto: `/internal/consumer/page/config/${id}`,
      type: ClowncarAppConstants.CONSUMER_PAGE_CONFIG_GET,
    },
  };
}

export function fetchPublishedConfigByDraftId(draftId: string): ApiAction {
  return {
    TockApi: {
      getProto: `/internal/consumer/page/config/draft/${draftId}`,
      type: ClowncarAppConstants.CONSUMER_PAGE_CONFIG_GET_BY_DRAFT,
    },
  };
}

export function clearConfig(): BaseTockAction {
  return {
    type: ClowncarAppConstants.CONSUMER_PAGE_CONFIG_CLEAR,
  };
}

export function updateDraftConfig(consumerPageConfig: clowncar.ConsumerPageConfig): ApiAction {
  const request = ConsumerPageConfig.fromObject(consumerPageConfig);
  return {
    TockApi: {
      putProto: '/internal/consumer/page/draft',
      type: ClowncarAppConstants.CONSUMER_PAGE_CONFIG_DRAFT_UPDATE,
      request,
    },
  };
}

export function publishDraftConfig(consumerPageConfig: clowncar.ConsumerPageConfig): ApiAction {
  const request = ConsumerPageConfig.fromObject(consumerPageConfig);
  return {
    TockApi: {
      postProto: '/internal/consumer/page/draft/publish',
      type: ClowncarAppConstants.CONSUMER_PAGE_CONFIG_UPDATE,
      request,
    },
  };
}

export function updateConfig(consumerPageConfig: clowncar.ConsumerPageConfig): ApiAction {
  const request = ConsumerPageConfig.fromObject(consumerPageConfig);
  return {
    TockApi: {
      putProto: '/internal/consumer/page/config',
      type: ClowncarAppConstants.CONSUMER_PAGE_CONFIG_UPDATE,
      request,
    },
  };
}

export function createDraftConfig(consumerPageConfig: clowncar.ConsumerPageConfig): ApiAction {
  const request = ConsumerPageConfig.fromObject(consumerPageConfig);
  return {
    TockApi: {
      postProto: '/internal/consumer/page/draft',
      type: ClowncarAppConstants.CONSUMER_PAGE_CONFIG_INSERT,
      request,
    },
  };
}

export function createConfig(consumerPageConfig: clowncar.ConsumerPageConfig): ApiAction {
  const request = ConsumerPageConfig.fromObject(consumerPageConfig);
  return {
    TockApi: {
      postProto: '/internal/consumer/page/config',
      type: ClowncarAppConstants.CONSUMER_PAGE_CONFIG_INSERT,
      request,
    },
  };
}

export function deleteConfig(id: number): ApiAction {
  return {
    TockApi: {
      deleteProto: `/internal/consumer/page/config/${id}`,
      type: ClowncarAppConstants.CONSUMER_PAGE_CONFIG_DELETE,
    },
  };
}

export function selectConsumerPageConfig(
  consumerPageConfig: clowncar.ConsumerPageConfig
): BaseTockAction {
  return {
    payload: consumerPageConfig,
    type: ClowncarAppConstants.CONSUMER_PAGE_CONFIG_SELECT,
  };
}

/**
 * Fetches all NamedConfigurations.
 *
 * @param {NamedConfirugationFetchMode} fetchMode The fetchMode filter.
 *  - `ACTIVE` (default) returns `NamedConfigurations` where `isArchived: false` only
 *  - `ARCHIVED` returns `NamedConfigurations` where `isArchived: true` only
 *  - `ALL` returns all `NamedConfigurations`
 * @returns {Object} A redux TockApi action.
 */
export function fetchAllNamedConfigurations(
  fetchMode: ConfigurationFetchMode = 'ACTIVE'
): ApiAction {
  return {
    TockApi: {
      getListProto: `/internal/consumer/page/config/named?fetchMode=${fetchMode}`,
      type: ClowncarAppConstants.CONSUMER_PAGE_NAMED_CONFIGS_GET_ALL,
    },
  };
}

export function fetchAllCollectionDataConfigurations(
  fetchMode: ConfigurationFetchMode = 'ACTIVE'
): ApiAction {
  return {
    TockApi: {
      getListProto: `/internal/consumer/page/config/collection?fetchMode=${fetchMode}`,
      type: ClowncarAppConstants.CONSUMER_PAGE_COLLECTION_DATA_CONFIGS_GET_ALL,
    },
  };
}

export function createDataConfiguration(config: consumer.ICollectionDataConfiguration): ApiAction {
  return {
    TockApi: {
      postProto: '/internal/consumer/page/config/collection',
      type: ClowncarAppConstants.CONSUMER_PAGE_COLLECTION_DATA_CONFIGS_INSERT,
      request: CollectionDataConfiguration.fromObject(config),
      toast: 'Created new data configuration',
    },
  };
}

export function updateDataConfiguration(config: consumer.ICollectionDataConfiguration): ApiAction {
  return {
    TockApi: {
      putProto: '/internal/consumer/page/config/collection',
      type: ClowncarAppConstants.CONSUMER_PAGE_COLLECTION_DATA_CONFIGS_PUT,
      request: CollectionDataConfiguration.fromObject(config),
      toast: 'Updated data configuration',
    },
  };
}

/**
 * Inserts a NamedConfiguration.
 *
 * @param {consumer.INamedConfiguration} config The NamedConfiguration object.
 * @returns {Object} A redux TockApi action.
 */
export function createNamedConfiguration(config: consumer.INamedConfiguration): ApiAction {
  const configProto = NamedConfiguration.fromObject(config);
  return {
    TockApi: {
      postProto: '/internal/consumer/page/config/named',
      type: ClowncarAppConstants.CONSUMER_PAGE_NAMED_CONFIGS_INSERT,
      request: configProto,
      toast: 'Created new named configuration',
    },
  };
}

/**
 * Updates an existing NamedConfiguration.
 *
 * @param {consumer.INamedConfiguration} config The NamedConfiguration object being updated.
 * @returns {Object} A redux TockApi action.
 */
export function updateNamedConfiguration(config: consumer.INamedConfiguration): ApiAction {
  const configProto = NamedConfiguration.fromObject(config);
  return {
    TockApi: {
      putProto: '/internal/consumer/page/config/named',
      type: ClowncarAppConstants.CONSUMER_PAGE_NAMED_CONFIGS_PUT,
      request: configProto,
      toast: 'Saved named configuration',
    },
  };
}

/**
 * Deletes an existing NamedConfiguration.
 *
 * @param {id} number The NamedConfiguration id being deleted.
 * @returns {Object} A redux TockApi action.
 */
export function deleteNamedConfiguration(id: number): ApiAction {
  return {
    TockApi: {
      deleteProto: `/internal/consumer/page/config/named/${id}`,
      type: ClowncarAppConstants.CONSUMER_PAGE_NAMED_CONFIGS_REMOVE,
      toast: 'Deleted named configuration',
    },
  };
}
